import { useEffect } from "react";

import { useDisclosure } from "@chakra-ui/react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { FeatureType } from "@/jenesys-types/data/enums";

import { useCompany } from "@/hooks/util/useCompany";
import useItemAction from "@/hooks/util/useItemAction";
import {
  loadAccountingData,
  loadBankConnections,
  loadIntegrationAuthorize,
  loadIntegrationDisconnect,
  loadIntegrationRefresh,
  loadLinkBusiness,
  loadPlatformStatus,
  selectConnectionsSettings
} from "@/store/features/clientSettings/connections/connections";
import {
  selectCompany,
  setCompany
} from "@/store/features/companies/companies";
import { accountingPlatforms, toastError, toastSuccess } from "@/util/helpers";

export const useCompanyIntegrations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const chooseBusinessDisclosure = useDisclosure();
  const chooseFetchDurationDisclosure = useDisclosure();

  const { selectedClient } = useCompany();
  const company = useSelector(selectCompany);

  const { clientId, companyId } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const authorized = searchParams.get("authorized");
  const error = searchParams.get("error");
  const platform = searchParams.get("platform");
  const connectionId = searchParams.get("connection_id");

  const {
    bankConnections,
    platformStatus,
    isFetchingStatus,
    isRefreshing,
    isLinkingSageBusiness,
    isFetchingBankConnections,
    isFetchingAcctAuthURL: isFetchingAuthURL,
    isDisconnectingIntegration: isDisconnecting
  } = useSelector(selectConnectionsSettings);

  const { response: accountingDataStatus } = useItemAction(
    "loadAccountingDataStatus",
    { companyId: clientId },
    undefined,
    FeatureType.Integrations,
    undefined,
    undefined,
    {
      enabled:
        !!selectedClient?.accounting_platform &&
        !!platformStatus &&
        platformStatus.name?.toLowerCase() ===
          selectedClient.accounting_platform.toLowerCase()
    }
  );

  useEffect(() => {
    if (clientId) {
      dispatch(loadPlatformStatus(clientId));
      dispatch(loadAccountingData(clientId));
      dispatch(loadBankConnections(clientId));
      dispatch(setCompany(clientId));
    }
  }, [dispatch, clientId]);

  // TODO: Check and update this if we need it, if not remove it- but confirm with product

  // const [socket] = useWebSocket(
  //   clientId && accessToken
  //     ? `${import.meta.env.VITE_BASE_WEBHOOK_URL}/ws?token=${accessToken}&company_id=${clientId}&event_type=fetch_accouting_data_status&x_company_id=${selectedCompany?.id}`
  //     : null,
  //   {
  //     onMessage: () => {
  //       dispatch(loadAccountingData(clientId!!));
  //     }
  //   }
  // );

  // useEffect(() => {
  //   if (socket && typeof socket !== "boolean") {
  //     (socket as WebSocket).onclose = () => {};
  //   }

  //   return () => {
  //     if (socket && typeof socket !== "boolean") {
  //       (socket as WebSocket).close();
  //     }
  //   };
  // }, [socket]);

  useEffect(() => {
    const userCancelled = error === "access_denied";
    const platformName = platform
      ? platform.charAt(0).toUpperCase() + platform.slice(1)
      : "";

    if (
      (authorized === "true" && platform === "sage") ||
      platform === "free_agent"
    ) {
      chooseBusinessDisclosure.onOpen();
      navigate(`/client/${clientId}/settings/connections`);
    } else if (authorized === "true" && platform === "yapily" && connectionId) {
      chooseFetchDurationDisclosure.onOpen();
      navigate(
        `/client/${clientId}/settings/connections?connection_id=${connectionId}`
      );
    } else if (authorized === "true" && platform) {
      toastSuccess(`${platformName} Integration successful`);
      navigate(`/client/${clientId}/settings/connections`);
    } else if (authorized === "false" && error) {
      toastError(
        userCancelled
          ? `${platformName} Connection Attempt Cancelled`
          : `${platformName} Integration failed`,
        null,
        error
      );
      navigate(`/client/${clientId}/settings/connections`);
    }
  }, [
    authorized,
    chooseBusinessDisclosure,
    chooseFetchDurationDisclosure,
    clientId,
    connectionId,
    error,
    navigate,
    platform
  ]);

  const refreshStatus = () => dispatch(loadPlatformStatus(clientId!!));

  const handleAuthorize = (platformName: string) =>
    dispatch(
      loadIntegrationAuthorize(clientId!!, platformName, (data: any) => {
        window.location.href = data.authorization_url;
      })
    );

  const handleRefresh = async (platformName: any) =>
    dispatch(
      loadIntegrationRefresh(
        clientId!!,
        platformName,
        () => {
          toastSuccess("Refreshed successfully!");
          refreshStatus();
        },
        () => {
          toastError("Refresh error!");
          refreshStatus();
        }
      )
    );

  const HandleDisconnect = async (payload: any) => {
    const type = "accounting";
    dispatch(
      loadIntegrationDisconnect(payload.company_id, payload.platform, type)
    );
  };

  const handleLinkBusiness = async (payload: any, onSuccess?: () => void) => {
    dispatch(
      loadLinkBusiness(
        payload.company_id,
        {
          business_id: payload.business_id,
          business_name: payload.business_name,
          company_id: payload.company_id,
          platform: payload.platform
        },
        () => {
          toastSuccess("Integration successful!");
          refreshStatus();
          if (onSuccess) onSuccess();
        }
      )
    );
  };

  const accountingIntegrations = platformStatus?.name
    ? [
        {
          ...platformStatus,
          platform: accountingPlatforms[platformStatus?.name?.toLowerCase()]
        }
      ]
    : Object.keys(accountingPlatforms).map((key) => ({
        platform: accountingPlatforms[key],
        name: key
      }));

  return {
    company,
    clientId,
    companyId,
    isRefreshing,
    bankConnections,
    isFetchingAuthURL,
    isDisconnecting,
    isLinkingSageBusiness,
    accountingIntegrations,
    chooseBusinessDisclosure,
    isFetchingStatus,
    accountingDataStatus,
    selectedClient,
    isFetchingBankConnections,
    chooseFetchDurationDisclosure,
    handleRefresh,
    handleAuthorize,
    handleLinkBusiness,
    HandleDisconnect
  };
};
